import React from 'react';
import { BaseComponentProps } from '../../types';
import { useAdminTranslation } from '../../modules/i18n';

interface AdminSidebarProps extends BaseComponentProps {
  isVisible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  hideHeader?: boolean;
  activeTab: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models';
  onTabChange: (tab: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models') => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  className = '',
  isVisible = true,
  isCollapsed = false,
  onToggleCollapse,
  hideHeader = false,
  activeTab,
  onTabChange
}) => {
  const { t } = useAdminTranslation();

  if (!isVisible) return null;

  const adminTabs = [
    {
      id: 'agents' as const,
      label: t('tabs.agents.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      ),
      description: t('tabs.agents.description')
    },
    {
      id: 'users' as const,
      label: t('tabs.users.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z'
          />
        </svg>
      ),
      description: t('tabs.users.description')
    },
    {
      id: 'documents' as const,
      label: t('tabs.documents.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          />
        </svg>
      ),
      description: t('tabs.documents.description')
    },
    // {
    //   id: 'sharepoint' as const,
    //   label: 'SharePoint',
    //   icon: (
    //     <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth={2}
    //         d='M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z'
    //       />
    //     </svg>
    //   ),
    //   description: t('tabs.sharepoint.description')
    // },
    {
      id: 'knowledgeGraph' as const,
      label: t('tabs.knowledgeGraph.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
          />
        </svg>
      ),
      description: t('tabs.knowledgeGraph.description')
    },
    {
      id: 'models' as const,
      label: t('tabs.models.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'
          />
        </svg>
      ),
      description: t('tabs.models.description')
    },
    // {
    //   id: 'facebook' as const,
    //   label: 'Facebook',
    //   icon: (...),
    //   description: t('tabs.facebook.description')
    // },
    // {
    //   id: 'zalo' as const,
    //   label: 'Zalo',
    //   icon: (...),
    //   description: t('tabs.zalo.description')
    // },
    // {
    //   id: 'teams' as const,
    //   label: 'Microsoft Teams',
    //   icon: (
    //     <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 16 16'>
    //       <path d='M9.186 4.797a2.42 2.42 0 1 0-2.86-2.448h1.178c.929 0 1.682.753 1.682 1.682zm-4.295 7.738h2.613c.929 0 1.682-.753 1.682-1.682V5.58h2.783a.7.7 0 0 1 .682.716v4.294a4.197 4.197 0 0 1-4.093 4.293c-1.618-.04-3-.99-3.667-2.35Zm10.737-9.372a1.674 1.674 0 1 1-3.349 0 1.674 1.674 0 0 1 3.349 0m-2.238 9.488-.12-.002a5.2 5.2 0 0 0 .381-2.07V6.306a1.7 1.7 0 0 0-.15-.725h1.792c.39 0 .707.317.707.707v3.765a2.6 2.6 0 0 1-2.598 2.598z'/>
    //       <path d='M.682 3.349h6.822c.377 0 .682.305.682.682v6.822a.68.68 0 0 1-.682.682H.682A.68.68 0 0 1 0 10.853V4.03c0-.377.305-.682.682-.682Zm5.206 2.596v-.72h-3.59v.72h1.357V9.66h.87V5.945z'/>
    //     </svg>
    //   ),
    //   description: t('tabs.teams.description')
    // },
  ];

  return (
    <div
      className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white transition-all duration-300 dark:border-border-dark dark:bg-dark-background-secondary ${
        isCollapsed ? 'w-16' : 'w-80'
      } ${className}`}
    >
      {/* Fixed Header */}
      {!hideHeader && (
        <div
          className={`flex flex-shrink-0 items-center border-b border-gray-200 dark:border-border-dark ${isCollapsed ? 'justify-center p-3' : 'justify-between p-4'}`}
        >
          <h2
            className={`font-semibold text-gray-900 transition-opacity duration-300 dark:text-dark-text ${
              isCollapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100'
            }`}
          >
            {t('sidebar.title')}
          </h2>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className='rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
              title={isCollapsed ? t('sidebar.expandTooltip') : t('sidebar.collapseTooltip')}
            >
              <svg
                className={`h-4 w-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Scrollable Navigation */}
      <div className='min-h-0 flex-1 overflow-y-auto scrollbar-thin'>
        <div className='p-2'>
          {adminTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`group flex w-full items-center gap-3 rounded-lg transition-colors duration-200 ${isCollapsed ? 'justify-center p-3' : 'p-3'} ${
                activeTab === tab.id
                  ? 'bg-primary text-white dark:bg-primary'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
              }`}
              style={{ minHeight: '60px' }}
              title={isCollapsed ? tab.label : undefined}
            >
              <div
                className={`flex-shrink-0 ${
                  activeTab === tab.id ? 'text-white' : 'text-gray-500 group-hover:text-gray-700 dark:text-dark-text-secondary dark:group-hover:text-dark-text-secondary'
                }`}
              >
                {tab.icon}
              </div>

              {!isCollapsed && (
                <div className='flex-1 text-left'>
                  <div className={`text-sm font-medium ${activeTab === tab.id ? 'text-white' : ''}`}>{tab.label}</div>
                  <div className={`mt-0.5 text-xs ${activeTab === tab.id ? 'text-white/80' : 'text-gray-500 dark:text-dark-text-secondary'}`}>
                    {tab.description}
                  </div>
                </div>
              )}

              {/* Active indicator */}
              {activeTab === tab.id && !isCollapsed && <div className='h-2 w-2 flex-shrink-0 rounded-full bg-white'></div>}
            </button>
          ))}
        </div>
      </div>

      {/* Fixed Footer */}
      {!isCollapsed && (
        <div className='flex-shrink-0 border-t border-gray-200 p-4 dark:border-border-dark'>
          <div className='text-xs text-gray-500 dark:text-dark-text-secondary'>
            <div className='mb-1 flex items-center gap-2'>
              <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <span>{t('sidebar.description')}</span>
            </div>
            <div className='text-gray-400 dark:text-dark-text-secondary'>{t('sidebar.subtitle')}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSidebar;
